import { useMutation } from '@tanstack/react-query';

import type { CompanySource } from '@sine/sine-core-api-types';
import { useNotification } from '@sine/sine-ui-components';

import parseServerError from '../../../helpers/parseServerError';
import { MutationKeys } from '../../../query.client';
import visitService from '../../../services/visit/visit.service';

interface IdentityCheckParams {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  company: string;
  companyId?: string | null;
  companySource?: CompanySource | null;
  siteId: string;
  zoneId?: string;
  visitorTypeId: string;
  invitationId?: string;
  adminNotes?: string | null;
  visitorNotes?: string | null;
}

export function useIdentityCheck() {
  let visitorsFullName = '';

  const { addNotification } = useNotification();

  const { mutateAsync: identityCheck, isLoading: isLoadingIdentityCheck } = useMutation({
    mutationKey: [MutationKeys.IDENTITY_CHECK],
    mutationFn: async (identityCheckValues: IdentityCheckParams) => {
      const {
        siteId,
        firstName,
        lastName,
        email,
        mobile,
        company,
        companyId,
        companySource,
        zoneId,
        visitorTypeId,
        adminNotes,
        visitorNotes,
        invitationId,
      } = identityCheckValues;

      visitorsFullName = `${firstName} ${lastName}`;

      const { token } = await visitService.identityCheck(siteId, {
        firstName,
        lastName,
        email,
        mobile,
        company: company,
        ...(companyId && { companyId }),
        ...(companySource && { companySource }),
        visitorType: visitorTypeId,
        ...(adminNotes && { adminNotes }),
        ...(visitorNotes && { visitorNotes }),
        invitationId,
        ...(zoneId && { zoneId }),
      });

      return token;
    },
    onError: (error: any) => {
      let errorHeading = '';

      if (error.code === 'CHECK_IN_NOT_PERMITTED') {
        errorHeading = `Sorry, we couldn't sign this person in!`;
        error.customMsg = 'This pass has been rejected';
      } else if (error.code === 'PASS_ALREADY_EXISTS' && error.info === 'PASS_PENDING_WITH_HOST') {
        error.customMsg = 'A pass is awaiting host approval';
      } else if (error.code === 'PASS_ALREADY_EXISTS') {
        error.customMsg = `${visitorsFullName} already checked in.`;
      }

      addNotification({
        heading: errorHeading,
        message: parseServerError(error),
        variant: 'danger',
      });
    },
  });

  return {
    identityCheck,
    isLoadingIdentityCheck,
  };
}
